import { useEffect, useState, useCallback } from 'react';
import { useApi, configApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { Badge, Tooltip } from '@material-ui/core';
import { alpha, makeStyles } from '@material-ui/core/styles';
import RouterIcon from '@material-ui/icons/Router';
import { manewcoTokens } from '@platform/plugin-platform-react';

const useStyles = makeStyles(theme => {
  const t = manewcoTokens(theme);
  return {
    connected: {
      backgroundColor: t.vividText.green,
      boxShadow: `0 0 6px ${alpha(t.vividText.green, 0.6)}`,
    },
    disconnected: {
      backgroundColor: t.vividText.red,
      boxShadow: `0 0 6px ${alpha(t.vividText.red, 0.6)}`,
    },
    noAgent: {
      backgroundColor: t.muted,
    },
  };
});

interface AgentInfo {
  id: string;
  name: string;
  status: 'connected' | 'disconnected';
  lastHeartbeat: string | null;
  version: string;
}

/**
 * Icône de l'entrée « Clusters » du menu, avec la pastille d'état des agents
 * Kubernetes. Elle vivait dans `packages/app` (`AgentStatusIndicator`), en
 * ligne au-dessus de l'entrée, et appelait le backend d'un module optionnel.
 * Le menu la rend comme un élément (`icon` de l'entrée) : c'est un composant
 * vivant. Tant que l'état est inconnu (chargement, appel en échec), pas de
 * pastille.
 */
export const AgentStatusIcon = () => {
  const classes = useStyles();
  const configApi = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [agents, setAgents] = useState<AgentInfo[] | null>(null);

  const backendUrl = configApi.getString('backend.baseUrl');

  const refresh = useCallback(() => {
    fetchApi
      .fetch(`${backendUrl}/api/agent-router/agents`)
      .then(async res => {
        if (!res.ok) return;
        const data = await res.json();
        setAgents(data);
      })
      .catch(() => {});
  }, [backendUrl, fetchApi]);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, 30_000);
    return () => clearInterval(interval);
  }, [refresh]);

  if (agents === null) return <RouterIcon />;

  const connected = agents.filter(a => a.status === 'connected');
  let dotClass = classes.noAgent;
  let tooltip = 'Aucun agent enregistré';
  if (connected.length > 0) {
    dotClass = classes.connected;
    tooltip = `${connected.length} agent(s) connecté(s) : ${connected.map(a => a.name).join(', ')}`;
  } else if (agents.length > 0) {
    dotClass = classes.disconnected;
    tooltip = `${agents.length} agent(s) enregistré(s), aucun connecté`;
  }

  return (
    <Tooltip title={tooltip} placement="right">
      <Badge variant="dot" overlap="rectangle" classes={{ badge: dotClass }}>
        <RouterIcon />
      </Badge>
    </Tooltip>
  );
};
